import React, { useState, useEffect } from "react";
import { Bell } from "lucide-react";
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SystemNotification } from "@/api/entities";
import { useAuth } from "@/lib/AuthContext";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";

export default function NotificationBell() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) return;

    setLoading(true);
    // Suscribirse a notificaciones en tiempo real
    const unsubscribe = SystemNotification.subscribe(
      (data) => {
        // Ordenar por fecha DESC
        const sorted = [...data].sort((a, b) => {
          const dateA = new Date(a.created_date || 0).getTime();
          const dateB = new Date(b.created_date || 0).getTime();
          return dateB - dateA;
        });
        setNotifications(sorted);
        setLoading(false);
        setError(null);
      },
      { user_id: user.uid },
      (err) => {
        console.error("Error en suscripción de notificaciones:", err);
        setError("Error al cargar notificaciones");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAllAsRead = async () => {
    const unread = notifications.filter(n => !n.read);
    const promises = unread.map(n => SystemNotification.update(n.id, { read: true }));
    try {
      await Promise.all(promises);
    } catch (error) {
      console.error("Error marcando notificaciones como leídas:", error);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200">
          <Bell className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 border-2 border-white dark:border-gray-950 text-[9px] font-bold text-white items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 mr-4" align="end">
        <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Notificaciones</h3>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 text-[11px] text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
              onClick={markAllAsRead}
            >
              Marcar todo como leído
            </Button>
          )}
        </div>
        <ScrollArea className="h-[350px]">
          {loading ? (
            <div className="p-8 text-center text-sm text-gray-500">
              <div className="animate-spin w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto mb-2" />
              Cargando...
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-500 text-sm">{error}</div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-12 h-12 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3">
                <Bell className="w-6 h-6 text-gray-300 dark:text-gray-600" />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">No tienes notificaciones</p>
            </div>
          ) : (
            <div className="flex flex-col">
              {notifications.map((n) => (
                <div 
                  key={n.id} 
                  className={`p-4 border-b border-gray-100 dark:border-gray-800 last:border-0 transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-800/50 ${!n.read ? 'bg-indigo-50/50 dark:bg-indigo-900/10 border-l-2 border-l-indigo-600' : ''}`}
                >
                  <div className="flex gap-3">
                    <div className="space-y-1 flex-1">
                      <p className={`text-sm leading-snug ${!n.read ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                        {n.message}
                      </p>
                      <p className="text-[10px] text-gray-500 underline-offset-2 flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${!n.read ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-700'}`} />
                        {n.created_date ? formatDistanceToNow(new Date(n.created_date), { addSuffix: true, locale: es }) : 'Recientemente'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
